import { useState } from "react";
import api, { eur } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Sparkle, EnvelopeSimple, FileText, Copy } from "@phosphor-icons/react";

function RecoConditions({ cs }) {
  if (!cs || !(cs.montant || cs.accompagnement_mois || cs.exclusivite_jours)) return null;
  return (
    <div className="flex flex-wrap gap-2 text-[11px]">
      {cs.montant ? <span className="bg-white border border-[#c7d2fe] px-2 py-0.5 rounded">Prix : <b>{eur(cs.montant)}</b></span> : null}
      {cs.accompagnement_mois ? <span className="bg-white border border-[#c7d2fe] px-2 py-0.5 rounded">Accompagnement : <b>{cs.accompagnement_mois} mois</b></span> : null}
      {cs.exclusivite_jours ? <span className="bg-white border border-[#c7d2fe] px-2 py-0.5 rounded">Exclusivité : <b>{cs.exclusivite_jours} j</b></span> : null}
    </div>
  );
}

function RecoBody({ reco, onGenerate, onUseInEmail, onUseInLoi }) {
  return (
    <div className="space-y-3 text-sm" data-testid="package-content">
      <div>
        <span className="inline-block text-[11px] font-bold uppercase tracking-wide bg-[#4f46e5] text-white px-2 py-0.5 rounded-full mb-2">{reco.package_name}</span>
        {reco.accroche && <p className="font-medium leading-snug">{reco.accroche}</p>}
      </div>

      {reco.available_packages && (
        <div className="flex flex-wrap gap-1">
          {reco.available_packages.map((p) => (
            <button key={p.key} data-testid={`package-switch-${p.key}`} onClick={() => onGenerate(p.key)}
              className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${p.key === reco.package_key ? "bg-[#4f46e5] text-white border-[#4f46e5]" : "border-[#c7d2fe] text-[#4f46e5] hover:bg-[#e0e7ff]"}`}>
              {p.name}
            </button>
          ))}
        </div>
      )}

      {reco.arguments && reco.arguments.length > 0 && (
        <ul className="space-y-1">
          {reco.arguments.map((a, i) => <li key={`arg-${i}-${a.slice(0, 16)}`} className="text-xs flex gap-1.5"><span className="text-[#4f46e5]">•</span>{a}</li>)}
        </ul>
      )}

      <RecoConditions cs={reco.conditions_suggerees} />

      {reco.message_approche && (
        <div className="bg-white/70 border border-[#c7d2fe] p-2.5">
          <div className="overline text-muted-foreground mb-1">Message d'approche suggéré</div>
          <p className="text-xs leading-relaxed whitespace-pre-line">{reco.message_approche}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <button data-testid="package-use-email" onClick={() => onUseInEmail?.(reco.message_approche)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary text-white hover:bg-blue-800">
          <EnvelopeSimple size={13} /> Insérer dans l'email
        </button>
        <button data-testid="package-use-loi" onClick={onUseInLoi}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-primary text-primary hover:bg-accent">
          <FileText size={13} /> Utiliser dans la LOI
        </button>
        <button data-testid="package-copy" onClick={() => { navigator.clipboard.writeText(reco.message_approche || ""); toast.success("Message copié"); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-border hover:bg-secondary">
          <Copy size={13} /> Copier
        </button>
      </div>
    </div>
  );
}

export default function PackageReco({ c, onUseInEmail, onUseInLoi }) {
  const [reco, setReco] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async (packageKey) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/companies/${c.id}/package-reco`, packageKey ? { package_key: packageKey } : {});
      setReco(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur préconisation"); }
    finally { setLoading(false); }
  };

  const useInLoi = () => {
    const cs = reco.conditions_suggerees || {};
    const clauses = (reco.arguments || []).join("\n");
    onUseInLoi?.({
      montant: cs.montant || "",
      exclusivite_jours: cs.exclusivite_jours || "",
      accompagnement_mois: cs.accompagnement_mois || "",
      clauses_extra: clauses,
    });
    toast.success("Conditions insérées dans la LOI (panneau DocuSign)");
  };

  return (
    <div className="ai-surface p-5" data-testid="package-reco">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Gift size={16} className="text-[#6366f1]" weight="fill" /><span className="overline text-[#4f46e5]">Préconisation de package</span></div>
        <button data-testid="package-generate-btn" onClick={() => generate()} disabled={loading}
          className="text-xs font-semibold text-[#4f46e5] hover:underline disabled:opacity-50">
          {loading ? "Analyse…" : reco ? "Régénérer" : "Suggérer un package"}
        </button>
      </div>

      {!reco ? (
        <p className="text-xs text-muted-foreground">L'IA propose un package d'acquisition/accompagnement adapté au profil du cabinet (transmission, croissance, continuité, redressement).</p>
      ) : (
        <RecoBody reco={reco} onGenerate={generate} onUseInEmail={onUseInEmail} onUseInLoi={useInLoi} />
      )}
    </div>
  );
}
